import * as React from 'react'
import styled from 'styled-components'
import ReactSelect from 'react-select'

interface IPostsOption {
  value: number
  label: string
}

interface IProps {
  posts: any[]
  selectedPostIds: number[]
  onChangePostIds: (postIds: number[]) => void
  placeholder?: string
  label?: string
  isMulti?: boolean
  isClearable?: boolean
  isSearchable?: boolean
  isFullLabel?: boolean
}

/**
 * ツアー種類（Post）を選択するコンポーネント
 * short_name/name に対して複数キーワード AND 検索に対応
 */
export const PostsSelector: React.FC<IProps> = React.memo(
  ({
    posts = [],
    selectedPostIds = [],
    onChangePostIds,
    placeholder = 'ツアー種類を検索（short_name / ツアー名 / 商品コード）',
    label = 'ツアー種類',
    isMulti = true,
    isClearable = true,
    isSearchable = true,
    isFullLabel = false,
  }) => {
    const [postSearchText, setPostSearchText] = React.useState('')

    const getLabel = (post: any) => {
      // DMCツアーは所要時間ごとに同名のPostがあるため、所要時間で区別できるようにする
      const duration = post.dmc_tour_id ? ` (${post.duration}h)` : ''
      if (isFullLabel) {
        return `${post.id} | [${post.bokun_product_code || '-'}] | ${post.short_name} | ${
          post.name
        }${duration}`
      }
      return `${post.short_name || post.name}${duration}`
    }

    // ツアー種類のoptions
    const postOptions = React.useMemo<IPostsOption[]>(
      () =>
        posts.map(post => ({
          value: Number(post.id),
          label: getLabel(post),
        })),
      [posts]
    )

    const selectedPostOptions = React.useMemo<IPostsOption[]>(
      () => postOptions.filter(option => selectedPostIds.includes(option.value)),
      [postOptions, selectedPostIds]
    )
    const selectedValue = isMulti ? selectedPostOptions : selectedPostOptions[0] || null

    // 検索用キャッシュマップ (post.id → 小文字化済みの short_name / name)
    interface IPostCache {
      short_name: string
      name: string
      product_code: string
    }
    const postCacheMap = React.useMemo(() => {
      const cacheMap = new Map<number, IPostCache>()
      posts.forEach(post => {
        cacheMap.set(post.id, {
          short_name: (post.short_name || '').toLowerCase(),
          name: (post.name || '').toLowerCase(),
          product_code: (post.bokun_product_code || '').toLowerCase() || '',
        })
      })
      return cacheMap
    }, [posts])

    // 半角/全角スペース区切りの複数キーワード AND 検索
    const customFilterOptionForPost = React.useCallback(
      (option: any, searchText: string) => {
        if (!searchText) {
          return true
        }

        const targetPost = postCacheMap.get(Number(option.value))
        if (!targetPost) {
          return false
        }

        const separated = searchText
          .toLowerCase()
          .split(/[\s　]+/)
          .filter(k => k.trim() !== '')

        return separated.every(query => {
          return (
            targetPost.short_name.includes(query) ||
            targetPost.name.includes(query) ||
            targetPost.product_code.includes(query)
          )
        })
      },
      [postCacheMap]
    )

    const handleChange = React.useCallback(
      (selected: IPostsOption | IPostsOption[] | null) => {
        const selectedOptions = Array.isArray(selected) ? selected : selected ? [selected] : []
        onChangePostIds(selectedOptions.map(option => option.value))
      },
      [onChangePostIds]
    )

    const handleInputChangePostIds = (inputValue: string, actionMeta: { action: string }) => {
      if (actionMeta.action === 'input-change' || actionMeta.action === 'set-value') {
        setPostSearchText(inputValue)
      }
    }

    return (
      <Wrapper>
        {label && <label>{label}</label>}
        <ReactSelect
          placeholder={placeholder}
          options={postOptions}
          isMulti={isMulti}
          isClearable={isClearable}
          isSearchable={isSearchable}
          filterOption={customFilterOptionForPost}
          inputValue={postSearchText}
          onInputChange={handleInputChangePostIds}
          onChange={handleChange}
          value={selectedValue}
          closeMenuOnSelect={!isMulti}
        />
      </Wrapper>
    )
  }
)

const Wrapper = styled.div`
  label {
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 4px;
  }
`
